import React from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MdAttachMoney, MdVerifiedUser, MdHourglassEmpty, MdWarningAmber, MdCancel } from "react-icons/md";

import Card from "components/card";
import Widget from "components/widget/Widget";
import { api } from "utils/api";
import { formatarData, formatarMoeda, ROTULOS_SITUACAO } from "./utils/formato";
import DetalheEmpresaModal from "./components/DetalheEmpresaModal";

const columnHelper = createColumnHelper();

export default function Assinantes() {
  const [metricas, setMetricas] = React.useState(null);
  const [empresas, setEmpresas] = React.useState([]);
  const [carregando, setCarregando] = React.useState(true);
  const [sorting, setSorting] = React.useState([]);

  const [busca, setBusca] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [plano, setPlano] = React.useState("");

  const [empresaSelecionada, setEmpresaSelecionada] = React.useState(null);

  const carregarMetricas = React.useCallback(async () => {
    const data = await api("/api/admin/metricas");
    if (data && data.success) setMetricas(data);
  }, []);

  const carregarEmpresas = React.useCallback(async () => {
    setCarregando(true);
    const params = new URLSearchParams();
    if (busca) params.set("busca", busca);
    if (status) params.set("status", status);
    if (plano) params.set("plano", plano);
    const data = await api("/api/admin/empresas?" + params.toString());
    if (data && data.success) setEmpresas(data.empresas);
    setCarregando(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, plano]);

  React.useEffect(() => {
    carregarMetricas();
    carregarEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function aplicarFiltros(e) {
    e?.preventDefault();
    carregarEmpresas();
  }

  function recarregarTudo() {
    carregarMetricas();
    carregarEmpresas();
  }

  const s = metricas?.por_situacao || {};

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("nome_fantasia", {
        id: "empresa",
        header: () => <p className="text-xs font-bold text-gray-600 dark:text-white">EMPRESA</p>,
        cell: (info) => (
          <div>
            <p className="text-sm font-bold text-navy-700 dark:text-white">{info.getValue()}</p>
            <p className="text-xs text-gray-500">{info.row.original.slug}</p>
          </div>
        ),
      }),
      columnHelper.accessor("plano", {
        id: "plano",
        header: () => <p className="text-xs font-bold text-gray-600 dark:text-white">PLANO</p>,
        cell: (info) => <p className="text-sm capitalize text-navy-700 dark:text-white">{info.getValue()}</p>,
      }),
      columnHelper.accessor("situacao", {
        id: "situacao",
        header: () => <p className="text-xs font-bold text-gray-600 dark:text-white">SITUAÇÃO</p>,
        cell: (info) => {
          const [rotulo, classe] = ROTULOS_SITUACAO[info.getValue()] || [info.getValue(), "bg-gray-100 text-gray-700"];
          return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${classe}`}>{rotulo}</span>;
        },
      }),
      columnHelper.accessor("data_vencimento", {
        id: "vencimento",
        header: () => <p className="text-xs font-bold text-gray-600 dark:text-white">VENCIMENTO</p>,
        cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{formatarData(info.getValue())}</p>,
      }),
      columnHelper.accessor("valor_mensal", {
        id: "valor",
        header: () => <p className="text-xs font-bold text-gray-600 dark:text-white">VALOR MENSAL</p>,
        cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{formatarMoeda(info.getValue())}</p>,
      }),
      columnHelper.accessor("data_cadastro", {
        id: "cadastro",
        header: () => <p className="text-xs font-bold text-gray-600 dark:text-white">CADASTRO</p>,
        cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{formatarData(info.getValue())}</p>,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: empresas,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div>
      <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Widget icon={<MdAttachMoney className="h-7 w-7" />} title="MRR" subtitle={metricas ? formatarMoeda(metricas.mrr) : "—"} />
        <Widget icon={<MdVerifiedUser className="h-6 w-6" />} title="Assinantes ativos" subtitle={(s.ativa || 0) + (s.vencida_carencia || 0)} />
        <Widget icon={<MdHourglassEmpty className="h-6 w-6" />} title="Em trial" subtitle={s.trial || 0} />
        <Widget icon={<MdWarningAmber className="h-6 w-6" />} title="Vencidas/bloqueadas" subtitle={(s.trial_vencido || 0) + (s.vencida_bloqueada || 0)} />
        <Widget icon={<MdCancel className="h-6 w-6" />} title="Canceladas" subtitle={s.cancelada || 0} />
      </div>

      <Card extra="mt-5 w-full p-4">
        <form className="grid grid-cols-1 gap-3 md:grid-cols-4" onSubmit={aplicarFiltros}>
          <input
            type="text"
            placeholder="Nome, slug ou e-mail"
            className="h-10 rounded-xl border border-gray-200 bg-white/0 px-3 text-sm dark:!border-white/10 dark:text-white md:col-span-2"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <select
            className="h-10 rounded-xl border border-gray-200 bg-white/0 px-3 text-sm dark:!border-white/10 dark:text-white"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Todas as situações</option>
            <option value="trial">Em trial</option>
            <option value="trial_vencido">Trial vencido (bloqueada)</option>
            <option value="ativa">Ativa</option>
            <option value="vencida_carencia">Vencida (carência)</option>
            <option value="vencida_bloqueada">Vencida (bloqueada)</option>
            <option value="cancelada">Cancelada / bloqueada</option>
            <option value="parceiro">Parceiro (free)</option>
          </select>
          <select
            className="h-10 rounded-xl border border-gray-200 bg-white/0 px-3 text-sm dark:!border-white/10 dark:text-white"
            value={plano}
            onChange={(e) => setPlano(e.target.value)}
          >
            <option value="">Todos os planos</option>
            <option value="basico">Básico</option>
            <option value="intermediario">Intermediário</option>
            <option value="equipe">Equipe</option>
            <option value="free">Free</option>
          </select>
          <button type="submit" className="h-10 rounded-xl bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600 dark:bg-brand-400 md:col-span-4 md:w-fit">
            Filtrar
          </button>
        </form>
      </Card>

      <Card extra="mt-5 w-full px-4 pb-4">
        <div className="mt-4 overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="!border-px !border-gray-200 dark:!border-white/10">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className="border-b border-gray-200 pb-2 pt-4 pr-4 text-start dark:border-white/10">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">Carregando...</td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">Nenhuma empresa encontrada.</td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5"
                    onClick={() => setEmpresaSelecionada(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="min-w-[120px] border-white/0 py-3 pr-4">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <DetalheEmpresaModal
        empresaId={empresaSelecionada}
        aberto={!!empresaSelecionada}
        onFechar={() => setEmpresaSelecionada(null)}
        onAlterado={recarregarTudo}
      />
    </div>
  );
}
